import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  CardActions,
  Grid,
  Button,
  Chip,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Alert,
  CircularProgress,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  RadioGroup,
  FormControlLabel,
  Radio
} from '@mui/material';
import {
  Science as ScienceIcon,
  AccountTree as ProfileIcon,
  Analytics as AnalyticsIcon,
  CheckCircle as CheckIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  NetworkCheck as NetworkIcon,
  AutoFixHigh as MagicIcon,
  TrendingUp as TrendingUpIcon,
  Groups as GroupsIcon,
  ExpandMore as ExpandMoreIcon,
  Lightbulb as LightbulbIcon,
  Rocket as RocketIcon
} from '@mui/icons-material';
import MainLayout from '../../layout/MainLayout';

const steps = [
  'Study Type Selection',
  'Profile Selection', 
  'Data Analysis & Mapping',
  'Results & Problem Resolution'
];

const DataMappingWizard = () => {
  const [activeStep, setActiveStep] = useState(0);
  const [selectedStudy, setSelectedStudy] = useState(null);
  const [selectedProfile, setSelectedProfile] = useState(null);
  const [analysisProgress, setAnalysisProgress] = useState(0);
  const [analysisComplete, setAnalysisComplete] = useState(false);
  const [analysisResults, setAnalysisResults] = useState(null);
  const [networkBoostActive, setNetworkBoostActive] = useState(false);
  const [problemResolutionDialog, setProblemResolutionDialog] = useState(false);
  const [selectedProblem, setSelectedProblem] = useState(null);
  const [mappingProcessDialog, setMappingProcessDialog] = useState(false);
  const [mappingProcessStep, setMappingProcessStep] = useState(0);

  // Mock data for study types
  const studyTypes = [
    {
      id: 'cardiovascular_toxicity',
      icon: '🫀',
      name: 'Cardiovascular Toxicity Study',
      description: 'Focus: Cardiac complications from cancer therapy',
      recommended_for: 'Chemotherapy cardiotoxicity research',
      vision_zero_compatible: true,
      estimated_coverage: 90
    },
    {
      id: 'cancer_outcomes',
      icon: '🎗️',
      name: 'Cancer Outcomes Research',
      description: 'Focus: Treatment response and survival analysis',
      recommended_for: 'Oncology outcome studies',
      vision_zero_compatible: true,
      estimated_coverage: 85
    },
    {
      id: 'multi_disease_epidemiology',
      icon: '🧬',
      name: 'Multi-Disease Epidemiology',
      description: 'Focus: Population health patterns',
      recommended_for: 'Large-scale epidemiological studies',
      vision_zero_compatible: true,
      estimated_coverage: 75
    }
  ];

  // Mock profile data
  const profiles = [
    {
      id: 'gold_cardiovascular_v2_1',
      name: 'GOLD Cardiovascular Research Bundle v2.1',
      type: 'recommended',
      profile_count: 5,
      usage_count: 23,
      vision_zero_compatible: true,
      estimated_coverage: 90,
      profiles: [
        'Patient (Cardiovascular Demographics)',
        'Condition (Heart Disease Classification)', 
        'Observation (Cardiac Vitals & Biomarkers)',
        'MedicationStatement (CV Medications)',
        'DiagnosticReport (Echo, EKG, Stress Tests)'
      ]
    },
    {
      id: 'charite_cardiotoxicity_template',
      name: 'Charité Cardiotoxicity Template v1.0',
      type: 'alternative',
      profile_count: 8,
      usage_count: 3,
      vision_zero_compatible: false,
      estimated_coverage: 85,
      profiles: [
        'GOLD Cardiovascular Bundle',
        'Charité Extensions'
      ]
    }
  ];

  const handleNext = () => {
    if (activeStep === 2 && !analysisComplete) {
      startAnalysis();
    } else {
      setActiveStep((prevActiveStep) => prevActiveStep + 1);
    }
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const startAnalysis = () => {
    setAnalysisProgress(0);
    
    const interval = setInterval(() => {
      setAnalysisProgress((prevProgress) => {
        const newProgress = prevProgress + 10;
        if (newProgress >= 100) {
          clearInterval(interval);
          setTimeout(() => {
            setAnalysisComplete(true);
            setAnalysisResults({
              total_parameters: 1247,
              cardiovascular_relevant: 847,
              gold_mappable: 798,
              coverage_percentage: 94,
              breakdown: {
                patient_demographics: { mapped: 23, total: 23, percentage: 100 },
                cardiovascular_conditions: { mapped: 156, total: 159, percentage: 98 },
                cardiac_observations: { mapped: 487, total: 507, percentage: 96 },
                medications: { mapped: 98, total: 110, percentage: 89 },
                diagnostic_reports: { mapped: 34, total: 48, percentage: 78 }
              },
              network_potential: {
                munich_boost: 84,
                hamburg_boost: 94,
                final_coverage: 94
              },
              problems: [
                {
                  id: 'charite_custom_001',
                  name: 'Cardiac Risk Assessment Score (1-10)',
                  issue: 'Proprietary scoring system without standard equivalent',
                  suggestions: [
                    { type: 'similar_standard', name: 'Glasgow Coma Scale', similarity: 78 },
                    { type: 'generic_mapping', confidence: 60 },
                    { type: 'expert_review', success_rate: 95, timeline: '3-5 days' },
                    { type: 'network_solution', source: 'München', description: 'APACHE II mapping', success_rate: 89 }
                  ]
                }
              ]
            });
          }, 500);
          return 100;
        }
        return newProgress;
      });
    }, 300);
  };

  const activateNetworkBoost = () => {
    setNetworkBoostActive(true);
    // Simulate network boost effect
    setTimeout(() => {
      setAnalysisResults(prev => ({
        ...prev,
        coverage_percentage: prev.network_potential.final_coverage,
        network_boost_applied: true
      }));
    }, 1000);
  };

  const startMappingProcess = () => {
    setMappingProcessDialog(true);
    setMappingProcessStep(0);
    
    // Simulate mapping process steps
    const steps = [
      'Initializing FHIR mapping engine...',
      'Loading GOLD profile templates...',
      'Processing cardiovascular parameters...',
      'Applying network intelligence mappings...',
      'Validating data transformations...',
      'Generating FHIR resources...',
      'Creating study configuration...',
      'Finalizing mapping deployment...'
    ];
    
    let currentStep = 0;
    const interval = setInterval(() => {
      currentStep++;
      if (currentStep <= steps.length) {
        setMappingProcessStep(currentStep);
      } else {
        clearInterval(interval);
        // After completion, show success and close
        setTimeout(() => {
          setMappingProcessDialog(false);
          // Could redirect to a new study page or show success message
          alert('🎉 Mapping Process Complete!\n\nYour cardiovascular toxicity study is now GOLD-standard ready and Vision Zero Consortium compatible.\n\nStudy ID: CARDIO-2024-001\nExpected Report Value: €50,000\n\nYou can now start collecting data with automated FHIR mapping!');
        }, 1500);
      }
    }, 1000);
  };

  const StudyTypeCard = ({ study }) => (
    <Card 
      sx={{ 
        height: '100%', 
        cursor: 'pointer',
        border: selectedStudy?.id === study.id ? 2 : 1,
        borderColor: selectedStudy?.id === study.id ? 'primary.main' : 'grey.300'
      }}
      onClick={() => setSelectedStudy(study)}
    >
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Typography variant="h4" sx={{ mr: 1 }}>{study.icon}</Typography>
          <Typography variant="h6" component="h2" sx={{ fontWeight: 600 }}>
            {study.name}
          </Typography>
        </Box>
        
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          {study.description}
        </Typography>
        
        <Typography variant="body2" sx={{ mb: 2 }}>
          Recommended for: {study.recommended_for}
        </Typography>
        
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          {study.vision_zero_compatible && (
            <Chip label="Vision Zero Compatible ✅" size="small" color="success" />
          )}
          <Chip label={`~${study.estimated_coverage}% Coverage`} size="small" color="info" />
        </Box>
      </CardContent>
      <CardActions>
        <Button 
          size="small" 
          variant={selectedStudy?.id === study.id ? 'contained' : 'outlined'}
        >
          {selectedStudy?.id === study.id ? 'Selected' : 'Select This Study Type'}
        </Button>
      </CardActions>
    </Card>
  );

  const ProfileCard = ({ profile }) => (
    <Card 
      sx={{ 
        mb: 2,
        cursor: 'pointer',
        border: selectedProfile?.id === profile.id ? 2 : 1,
        borderColor: selectedProfile?.id === profile.id ? 'primary.main' : 'grey.300'
      }}
      onClick={() => setSelectedProfile(profile)}
    >
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {profile.type === 'recommended' ? '⭐' : profile.type === 'alternative' ? '🏥' : '⚙️'} {profile.name}
          </Typography>
          <RadioGroup value={selectedProfile?.id || ''}>
            <FormControlLabel 
              value={profile.id} 
              control={<Radio />} 
              label={profile.type === 'recommended' ? 'Recommended' : 'Alternative'}
            />
          </RadioGroup>
        </Box>
        
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {profile.profile_count} FHIR Profiles | {profile.vision_zero_compatible ? 'Vision Zero Compatible' : 'Local Extensions'} | Used in {profile.usage_count} studies
        </Typography>
        
        <List dense>
          {profile.profiles.map((p, index) => (
            <ListItem key={index} sx={{ py: 0 }}>
              <ListItemText primary={`• ${p}`} />
            </ListItem>
          ))}
        </List>
        
        <Typography variant="body2" color="success.main" sx={{ mt: 1 }}>
          Expected Coverage: ~{profile.estimated_coverage}% (estimated)
        </Typography>
      </CardContent>
    </Card>
  );

  const ProblemResolutionDialog = () => (
    <Dialog open={problemResolutionDialog} onClose={() => setProblemResolutionDialog(false)} maxWidth="md" fullWidth>
      <DialogTitle>
        💡 Problem Resolution: {selectedProblem?.name}
      </DialogTitle>
      <DialogContent>
        {selectedProblem && (
          <Box>
            <Alert severity="warning" sx={{ mb: 2 }}>
              Issue: {selectedProblem.issue}
            </Alert>
            
            <Typography variant="h6" gutterBottom>Resolution Options:</Typography>
            
            {selectedProblem.suggestions.map((suggestion, index) => (
              <Card key={index} sx={{ mb: 2 }}>
                <CardContent>
                  {suggestion.type === 'similar_standard' && (
                    <Box>
                      <Typography variant="subtitle1">🔄 Use similar standard</Typography>
                      <Typography variant="body2">{suggestion.name} - {suggestion.similarity}% similar</Typography>
                    </Box>
                  )}
                  {suggestion.type === 'generic_mapping' && (
                    <Box>
                      <Typography variant="subtitle1">⚗️ Generic Observation mapping</Typography>
                      <Typography variant="body2">{suggestion.confidence}% confidence with custom coding</Typography>
                    </Box>
                  )}
                  {suggestion.type === 'expert_review' && (
                    <Box>
                      <Typography variant="subtitle1">👨‍⚕️ Request expert review</Typography>
                      <Typography variant="body2">{suggestion.success_rate}% success rate, {suggestion.timeline}</Typography>
                    </Box>
                  )}
                  {suggestion.type === 'network_solution' && (
                    <Box>
                      <Typography variant="subtitle1">🌐 Network Insight: {suggestion.source} solved similar case</Typography>
                      <Typography variant="body2">{suggestion.description} - {suggestion.success_rate}% success rate</Typography>
                      <Button variant="contained" size="small" sx={{ mt: 1 }}>
                        Apply {suggestion.source} Solution
                      </Button>
                    </Box>
                  )}
                </CardContent>
              </Card>
            ))}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setProblemResolutionDialog(false)}>Close</Button>
        <Button variant="contained">Apply Selected Solution</Button>
      </DialogActions>
    </Dialog>
  );

  const MappingProcessDialog = () => {
    const steps = [
      'Initializing FHIR mapping engine...',
      'Loading GOLD profile templates...',
      'Processing cardiovascular parameters...',
      'Applying network intelligence mappings...',
      'Validating data transformations...',
      'Generating FHIR resources...',
      'Creating study configuration...',
      'Finalizing mapping deployment...'
    ];
    
    return (
      <Dialog open={mappingProcessDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          🚀 Starting Data Mapping Process
        </DialogTitle>
        <DialogContent>
          <Box sx={{ py: 2 }}>
            <Alert severity="info" sx={{ mb: 3 }}>
              Deploying your cardiovascular toxicity study with GOLD-standard mapping...
            </Alert>
            
            <Typography variant="h6" gutterBottom>
              Processing Steps:
            </Typography>
            
            {steps.map((step, index) => (
              <Box key={index} sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                {index < mappingProcessStep ? (
                  <CheckIcon color="success" sx={{ mr: 2 }} />
                ) : index === mappingProcessStep ? (
                  <CircularProgress size={20} sx={{ mr: 2 }} />
                ) : (
                  <Box sx={{ width: 20, height: 20, mr: 2 }} />
                )}
                <Typography 
                  variant="body2" 
                  sx={{ 
                    color: index <= mappingProcessStep ? 'text.primary' : 'text.secondary',
                    fontWeight: index === mappingProcessStep ? 600 : 400
                  }}
                >
                  {step}
                </Typography>
              </Box>
            ))}
            
            <Box sx={{ mt: 3 }}>
              <LinearProgress 
                variant="determinate" 
                value={(mappingProcessStep / steps.length) * 100} 
                sx={{ mb: 1 }}
              />
              <Typography variant="body2" color="text.secondary" align="center">
                {mappingProcessStep}/{steps.length} steps completed
              </Typography>
            </Box>
            
            {mappingProcessStep >= steps.length && (
              <Alert severity="success" sx={{ mt: 2 }}>
                ✅ Mapping process completed successfully!
              </Alert>
            )}
          </Box>
        </DialogContent>
      </Dialog>
    );
  };

  return (
    <MainLayout>
      <Box sx={{ width: '100%' }}>
        {/* Header */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h4" component="h1" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
            <MagicIcon /> GOLD Data Mapping Engine
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mt: 1 }}>
            Transform your hospital data to GOLD standards in minutes - the "rocket backwards from the sky"
          </Typography>
        </Box>

        {/* Stepper */}
        <Stepper activeStep={activeStep} orientation="vertical">
          {/* Step 1: Study Type Selection */}
          <Step>
            <StepLabel>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <ScienceIcon />
                New Research Study Setup
              </Box>
            </StepLabel>
            <StepContent>
              <Typography sx={{ mb: 2 }}>
                What type of study are you conducting?
              </Typography>
              
              <Grid container spacing={3}>
                {studyTypes.map((study) => (
                  <Grid item xs={12} md={4} key={study.id}>
                    <StudyTypeCard study={study} />
                  </Grid>
                ))}
              </Grid>
              
              <Box sx={{ mb: 1, mt: 3 }}>
                <Button
                  variant="contained"
                  onClick={handleNext}
                  disabled={!selectedStudy}
                  sx={{ mr: 1 }}
                >
                  Continue with {selectedStudy?.name || 'Selected Study'} →
                </Button>
              </Box>
            </StepContent>
          </Step>

          {/* Step 2: Profile Selection */}
          <Step>
            <StepLabel>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <ProfileIcon />
                FHIR Profile Selection
              </Box>
            </StepLabel>
            <StepContent>
              <Alert severity="info" sx={{ mb: 2 }}>
                Study: {selectedStudy?.icon} {selectedStudy?.name}
              </Alert>
              
              <Typography variant="h6" gutterBottom>
                Recommended Profile Sets (from your repository):
              </Typography>
              
              {profiles.map((profile) => (
                <ProfileCard key={profile.id} profile={profile} />
              ))}
              
              <Box sx={{ mb: 1, mt: 2 }}>
                <Button onClick={handleBack} sx={{ mr: 1 }}>
                  ← Back
                </Button>
                <Button
                  variant="contained"
                  onClick={handleNext}
                  disabled={!selectedProfile}
                >
                  Continue to Data Analysis →
                </Button>
              </Box>
            </StepContent>
          </Step>

          {/* Step 3: Data Analysis & Mapping */}
          <Step>
            <StepLabel>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <AnalyticsIcon />
                ARGUS Data Analysis
              </Box>
            </StepLabel>
            <StepContent>
              <Alert severity="info" sx={{ mb: 2 }}>
                Hospital: Charité Berlin | Profile Set: {selectedProfile?.name}
              </Alert>
              
              {!analysisComplete ? (
                <Box>
                  <Typography variant="h6" gutterBottom>
                    Analyzing hospital parameters...
                  </Typography>
                  <LinearProgress variant="determinate" value={analysisProgress} sx={{ mb: 2 }} />
                  <Typography variant="body2" color="text.secondary">
                    Progress: {analysisProgress}%
                  </Typography>
                </Box>
              ) : (
                <Box>
                  <Alert severity="success" sx={{ mb: 2 }}>
                    📊 Analysis Complete! Found 1,247 parameters, 798 are GOLD-mappable (94% coverage)
                  </Alert>
                  
                  <Grid container spacing={2} sx={{ mb: 3 }}>
                    <Grid item xs={3}>
                      <Paper sx={{ p: 2, textAlign: 'center' }}>
                        <Typography variant="h4" color="primary.main">1,247</Typography>
                        <Typography variant="body2">Total Parameters</Typography>
                      </Paper>
                    </Grid>
                    <Grid item xs={3}>
                      <Paper sx={{ p: 2, textAlign: 'center' }}>
                        <Typography variant="h4" color="info.main">847</Typography>
                        <Typography variant="body2">CV Relevant</Typography>
                      </Paper>
                    </Grid>
                    <Grid item xs={3}>
                      <Paper sx={{ p: 2, textAlign: 'center' }}>
                        <Typography variant="h4" color="success.main">798</Typography>
                        <Typography variant="body2">GOLD-Mappable</Typography>
                      </Paper>
                    </Grid>
                    <Grid item xs={3}>
                      <Paper sx={{ p: 2, textAlign: 'center' }}>
                        <Typography variant="h4" color="success.main">{analysisResults?.coverage_percentage}%</Typography>
                        <Typography variant="body2">Coverage</Typography>
                      </Paper>
                    </Grid>
                  </Grid>

                  <Typography variant="h6" gutterBottom>Breakdown by Profile:</Typography>
                  <List>
                    {Object.entries(analysisResults?.breakdown || {}).map(([key, data]) => (
                      <ListItem key={key}>
                        <ListItemIcon>
                          {data.percentage >= 95 ? <CheckIcon color="success" /> : 
                           data.percentage >= 85 ? <WarningIcon color="warning" /> : 
                           <ErrorIcon color="error" />}
                        </ListItemIcon>
                        <ListItemText 
                          primary={`${key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}: ${data.percentage}%`}
                          secondary={`${data.mapped}/${data.total} parameters`}
                        />
                      </ListItem>
                    ))}
                  </List>

                  {!networkBoostActive && (
                    <Alert severity="info" sx={{ mb: 2 }}>
                      🌐 Network Intelligence Available: München Klinikum and Hamburg UKE have similar mappings.
                      Network Boost Potential: {analysisResults?.coverage_percentage}% → {analysisResults?.network_potential?.final_coverage}%
                    </Alert>
                  )}

                  {networkBoostActive && (
                    <Alert severity="success" sx={{ mb: 2 }}>
                      ✨ Network Boost Applied! Coverage improved to {analysisResults?.network_potential?.final_coverage}%
                    </Alert>
                  )}
                </Box>
              )}
              
              <Box sx={{ mb: 1, mt: 2 }}>
                <Button onClick={handleBack} sx={{ mr: 1 }}>
                  ← Back
                </Button>
                {!analysisComplete ? (
                  <Button variant="contained" onClick={startAnalysis} startIcon={<RocketIcon />}>
                    Start Analysis
                  </Button>
                ) : (
                  <>
                    {!networkBoostActive && (
                      <Button 
                        variant="outlined" 
                        onClick={activateNetworkBoost} 
                        startIcon={<NetworkIcon />}
                        sx={{ mr: 1 }}
                      >
                        Activate Network Boost
                      </Button>
                    )}
                    <Button variant="contained" onClick={handleNext}>
                      View Detailed Mappings →
                    </Button>
                  </>
                )}
              </Box>
            </StepContent>
          </Step>

          {/* Step 4: Results & Problem Resolution */}
          <Step>
            <StepLabel>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CheckIcon />
                Results & Problem Resolution
              </Box>
            </StepLabel>
            <StepContent>
              <Alert severity="success" sx={{ mb: 2 }}>
                Study: {selectedStudy?.name} | Final Coverage: {analysisResults?.coverage_percentage}%{networkBoostActive ? ' (with network)' : ''}
              </Alert>
              
              <Typography variant="h6" gutterBottom>✅ Successfully Mapped Parameters (798):</Typography>
              <TableContainer component={Paper} sx={{ mb: 3 }}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>ARGUS Parameter</TableCell>
                      <TableCell>GOLD FHIR Target</TableCell>
                      <TableCell>Confidence</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    <TableRow>
                      <TableCell>MIMIC_220045<br /><Typography variant="body2" color="text.secondary">(Heart Rate)</Typography></TableCell>
                      <TableCell>Observation<br /><Typography variant="body2" color="text.secondary">[HeartRate]</Typography></TableCell>
                      <TableCell><Chip label="99% ✅ Auto" color="success" size="small" /></TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>MIMIC_220179<br /><Typography variant="body2" color="text.secondary">(BP Systolic)</Typography></TableCell>
                      <TableCell>Observation<br /><Typography variant="body2" color="text.secondary">[BloodPressure]</Typography></TableCell>
                      <TableCell><Chip label="97% ✅ Auto" color="success" size="small" /></TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>

              <Typography variant="h6" gutterBottom>⚠️ Issues Requiring Attention (2):</Typography>
              {analysisResults?.problems?.map((problem) => (
                <Accordion key={problem.id}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <ErrorIcon color="error" />
                      <Typography>❌ {problem.name}</Typography>
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Typography variant="body2" sx={{ mb: 2 }}>
                      Issue: {problem.issue}
                    </Typography>
                    
                    <Typography variant="subtitle2" gutterBottom>💡 Resolution Options:</Typography>
                    <List dense>
                      {problem.suggestions.map((suggestion, index) => (
                        <ListItem key={index}>
                          <ListItemIcon><LightbulbIcon /></ListItemIcon>
                          <ListItemText 
                            primary={
                              suggestion.type === 'network_solution' ? 
                              `🌐 ${suggestion.source} solved similar case with ${suggestion.description}` :
                              `${suggestion.type.replace(/_/g, ' ')}`
                            }
                            secondary={
                              suggestion.success_rate ? `${suggestion.success_rate}% success rate` :
                              suggestion.confidence ? `${suggestion.confidence}% confidence` :
                              suggestion.similarity ? `${suggestion.similarity}% similarity` : ''
                            }
                          />
                        </ListItem>
                      ))}
                    </List>
                    
                    <Button 
                      variant="contained" 
                      size="small" 
                      onClick={() => {
                        setSelectedProblem(problem);
                        setProblemResolutionDialog(true);
                      }}
                    >
                      Resolve Issue
                    </Button>
                  </AccordionDetails>
                </Accordion>
              ))}

              <Alert severity="info" sx={{ mt: 3, mb: 2 }}>
                🎯 Study Configuration Summary:
                <br />• Vision Zero Consortium Compatible: ✅
                <br />• Multi-Hospital Network Ready: ✅
                <br />• Estimated Report Value: €50,000 (vs €10,000 standard)
              </Alert>
              
              <Box sx={{ mb: 1 }}>
                <Button onClick={handleBack} sx={{ mr: 1 }}>
                  ← Back
                </Button>
                <Button variant="outlined" sx={{ mr: 1 }}>
                  Save Configuration
                </Button>
                <Button variant="contained" startIcon={<RocketIcon />} onClick={startMappingProcess}>
                  Start Data Mapping Process →
                </Button>
              </Box>
            </StepContent>
          </Step>
        </Stepper>

        <ProblemResolutionDialog />
        <MappingProcessDialog />
      </Box>
    </MainLayout>
  );
};

export default DataMappingWizard;